import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Eye, EyeOff, Trash2, X, Loader2, Users, Upload, Download } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const KEY_STORAGE = "ditch_admin_key";
const TYPES = ["Full-time", "Contract", "Part-time", "Casual"];
const EMPTY = { title: "", city: "", salary_range: "", type: "Full-time", description: "" };
const headers = () => ({ "X-API-Key": sessionStorage.getItem(KEY_STORAGE) });
const inputCls = "mt-1 w-full rounded-lg border border-[#e3e0d3] bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]";
const labelCls = "text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]";

export const AdManager = () => {
  const [roles, setRoles] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} (new) | role (edit)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [applicantsFor, setApplicantsFor] = useState(null); // role being viewed
  const [applicants, setApplicants] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/roles", { headers: headers() });
      setRoles(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setRoles([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (role) => { setForm({ ...EMPTY, ...role }); setEditing(role); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title.trim() || !form.city.trim() || !form.description.trim()) {
      return toast.error("Title, city and description are required");
    }
    setSaving(true);
    try {
      const payload = { title: form.title, city: form.city, salary_range: form.salary_range, type: form.type, description: form.description };
      if (editing.id) {
        await api.put(`/admin/roles/${editing.id}`, payload, { headers: headers() });
        toast.success("Role updated");
      } else {
        await api.post("/admin/roles", payload, { headers: headers() });
        toast.success("Role published");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (role) => {
    const next = role.status === "published" ? "unpublished" : "published";
    try {
      await api.put(`/admin/roles/${role.id}`, { status: next }, { headers: headers() });
      toast.success(next === "published" ? "Role published" : "Role unpublished");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (role) => {
    if (!window.confirm(`Delete "${role.title}" permanently?`)) return;
    try {
      await api.delete(`/admin/roles/${role.id}`, { headers: headers() });
      toast.success("Role deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const viewApplicants = async (role) => {
    setApplicantsFor(role);
    setApplicants(null);
    try {
      const r = await api.get(`/admin/roles/${role.id}/applicants`, { headers: headers() });
      setApplicants(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setApplicants([]);
    }
  };

  const [backfilling, setBackfilling] = useState(false);
  const backfillRoleBriefs = async () => {
    if (!window.confirm("Re-push ALL existing role briefs to the CRM? Only do this once the CRM's /api/external/role-request endpoint is live.")) return;
    setBackfilling(true);
    try {
      const r = await api.post("/admin/backfill-role-requests", null, { headers: headers() });
      toast.success(`Pushed ${r.data.synced} of ${r.data.found} role brief(s)${r.data.failed ? ` · ${r.data.failed} failed` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBackfilling(false); }
  };

  const exportApplicantsCsv = () => {
    if (!applicants || applicants.length === 0) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["Name", "Email", "Role", "Applied"]];
    applicants.forEach((a) => rows.push([a.name, a.email, a.role_title || applicantsFor.title,
      a.created_at ? new Date(a.created_at).toLocaleDateString("en-AU") : ""]));
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `applicants-${(applicantsFor.title || "role").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="rounded-2xl border border-[#e3e0d3] bg-white p-8 shadow-sm" data-testid="ad-manager-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#0d2f4d]">Ad Manager</h2>
          <p className="mt-1 text-sm text-[#51637a]">Create, edit and unpublish roles on the live job board.</p>
        </div>
        <button onClick={openNew} data-testid="ad-new-role-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-[#0d2f4d] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#0a2540]">
          <Plus className="h-4 w-4" /> New role
        </button>
      </div>
      <button onClick={backfillRoleBriefs} disabled={backfilling} data-testid="ad-backfill-briefs-btn"
        className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#7C8514] px-4 py-2 text-xs font-medium text-[#5c6410] hover:bg-[#7C8514] hover:text-white disabled:opacity-60">
        <Upload className={`h-3.5 w-3.5 ${backfilling ? "animate-pulse" : ""}`} />
        {backfilling ? "Pushing…" : "Backfill role briefs to CRM"}
      </button>

      {roles === null ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0d2f4d]" /></div>
      ) : roles.length === 0 ? (
        <p className="mt-6 text-sm text-[#51637a]">No roles yet — click “New role” to post your first job.</p>
      ) : (
        <div className="mt-5 divide-y divide-[#f0ede4]" data-testid="ad-roles-list">
          {roles.map((role) => (
            <div key={role.id} className="flex flex-wrap items-center justify-between gap-3 py-3.5" data-testid={`ad-role-${role.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-[#0d2f4d]">{role.title}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                    role.status === "published" ? "bg-[#e6ecc4] text-[#5c6410]" : "bg-[#f0ede4] text-[#8a8578]"}`}>
                    {role.status}
                  </span>
                </div>
                <p className="text-xs text-[#51637a]">{role.city} · {role.type} · {role.applications || 0} applicant(s)</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => viewApplicants(role)} data-testid={`ad-applicants-${role.id}`} title="View applicants"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#e3e0d3] px-2.5 py-2 text-xs font-medium text-[#0d2f4d] hover:bg-[#f5f3ee]">
                  <Users className="h-4 w-4" /> {role.applications || 0}
                </button>
                <button onClick={() => toggleStatus(role)} data-testid={`ad-toggle-${role.id}`} title={role.status === "published" ? "Unpublish" : "Publish"}
                  className="rounded-lg border border-[#e3e0d3] p-2 text-[#0d2f4d] hover:bg-[#f5f3ee]">
                  {role.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button onClick={() => openEdit(role)} data-testid={`ad-edit-${role.id}`} title="Edit"
                  className="rounded-lg border border-[#e3e0d3] p-2 text-[#0d2f4d] hover:bg-[#f5f3ee]"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(role)} data-testid={`ad-delete-${role.id}`} title="Delete"
                  className="rounded-lg border border-[#e3e0d3] p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="ad-editor-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-7 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-[#0d2f4d]">{editing.id ? "Edit role" : "New role"}</h3>
              <button onClick={() => setEditing(null)} data-testid="ad-close-editor" className="text-[#51637a] hover:text-[#0d2f4d]"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className={labelCls}>Title</label>
                <input value={form.title} onChange={set("title")} data-testid="ad-field-title" className={inputCls} placeholder="e.g. Senior Chef" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>City</label>
                  <input value={form.city} onChange={set("city")} data-testid="ad-field-city" className={inputCls} placeholder="Sydney, NSW" />
                </div>
                <div>
                  <label className={labelCls}>Type</label>
                  <select value={form.type} onChange={set("type")} data-testid="ad-field-type" className={inputCls}>
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Salary range</label>
                <input value={form.salary_range} onChange={set("salary_range")} data-testid="ad-field-salary" className={inputCls} placeholder="$85k–$95k + super" />
              </div>
              <div>
                <label className={labelCls}>Description</label>
                <textarea value={form.description} onChange={set("description")} data-testid="ad-field-description" rows={5} className={inputCls} placeholder="What the role involves, who they're looking for…" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="rounded-lg border border-[#e3e0d3] px-5 py-2.5 text-sm font-medium text-[#0d2f4d] hover:bg-[#f5f3ee]">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="ad-save-role" className="inline-flex items-center gap-2 rounded-lg bg-[#C1CC24] px-5 py-2.5 text-sm font-semibold text-[#0d2f4d] hover:bg-[#aeb81f] disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing.id ? "Save changes" : "Publish role"}
              </button>
            </div>
          </div>
        </div>
      )}
      {applicantsFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="ad-applicants-modal">
          <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-2xl bg-white p-7 shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-[#0d2f4d]">Applicants</h3>
                <p className="text-xs text-[#51637a]">{applicantsFor.title} · {applicantsFor.city}</p>
              </div>
              <button onClick={() => setApplicantsFor(null)} data-testid="ad-close-applicants" className="text-[#51637a] hover:text-[#0d2f4d]"><X className="h-5 w-5" /></button>
            </div>
            {applicants && applicants.length > 0 && (
              <button onClick={exportApplicantsCsv} data-testid="ad-export-csv"
                className="mt-3 inline-flex items-center gap-2 self-start rounded-lg border border-[#0d2f4d] px-3.5 py-2 text-xs font-medium text-[#0d2f4d] hover:bg-[#0d2f4d] hover:text-white">
                <Download className="h-3.5 w-3.5" /> Download CSV
              </button>
            )}
            <div className="mt-5 flex-1 overflow-y-auto">
              {applicants === null ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0d2f4d]" /></div>
              ) : applicants.length === 0 ? (
                <p className="py-8 text-center text-sm text-[#51637a]" data-testid="ad-applicants-empty">No applicants yet for this role.</p>
              ) : (
                <div className="divide-y divide-[#f0ede4]" data-testid="ad-applicants-list">
                  {applicants.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <p className="font-medium text-[#0d2f4d]">{a.name || "—"}</p>
                        <a href={`mailto:${a.email}`} className="text-xs text-[#7C8514] hover:underline">{a.email}</a>
                      </div>
                      <span className="shrink-0 text-xs text-[#51637a]">
                        {a.created_at ? new Date(a.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
